import {
  AdditiveBlending,
  BoxGeometry,
  BufferAttribute,
  CanvasTexture,
  Color,
  CylinderGeometry,
  Group,
  LatheGeometry,
  type Material,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PlaneGeometry,
  PointLight,
  SphereGeometry,
  Vector2,
  Vector3,
} from 'three';
import { EMIT, LIGHTS } from '../render/palette';
import {
  MATERIALS,
  applyContactShading,
  emitter,
  flatVertexColor,
  signMaterial,
  type SignOptions,
} from '../render/materials';

/**
 * The prop kit. Everything the mall is built from lives here as a function that
 * returns a Group, so the level is data plus these builders — no imported models.
 *
 * Two conventions hold throughout:
 *   - Props are authored at the origin, footprint centred, sitting ON y=0.
 *   - Anything that emits light also registers a light pool so the floor reads
 *     correctly even when we cannot afford a real light.
 */

const V = (x: number, y: number, z: number) => new Vector3(x, y, z);

function box(w: number, h: number, d: number, mat: Material, contact = true) {
  const g = new BoxGeometry(w, h, d);
  if (contact) applyContactShading(g, { falloff: Math.max(0.6, h), strength: 0.35 });
  else flatVertexColor(g);
  const m = new Mesh(g, mat);
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

// ---------------------------------------------------------------------------
// Light pools — the backbone of the whole look
// ---------------------------------------------------------------------------

let poolTexture: CanvasTexture | null = null;
let glowMaterial: MeshBasicMaterial | null = null;

/**
 * THE ONE MATERIAL EVERY POOL AND WASH SHARES.
 *
 * These used to clone a material each, so the colour could live in
 * `material.color`. A hundred and seventy quads, a hundred and seventy
 * materials, a hundred and seventy guaranteed draw calls — the second largest
 * item in the map's draw budget, behind the loot containers and ahead of the
 * entire building shell after batching.
 *
 * Moving the colour into a vertex attribute makes them all the same object,
 * which lets `mergeStatic` collapse them per cell. That is only legal because
 * the blending is additive and addition commutes; see `isOrderFree`. The colour
 * is baked pre-multiplied by intensity and may exceed 1, which is correct — this
 * is a linear HDR value going into a tonemapper, not a paint swatch.
 */
function glowMat(): MeshBasicMaterial {
  if (!poolTexture) poolTexture = makePoolTexture();
  if (!glowMaterial)
    glowMaterial = new MeshBasicMaterial({
      map: poolTexture,
      vertexColors: true,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
      toneMapped: true,
      fog: true,
    });
  return glowMaterial;
}

/** Paint a geometry a single linear colour, unclamped. */
function paint(geo: PlaneGeometry, c: Color): PlaneGeometry {
  const n = geo.getAttribute('position').count;
  const a = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) a.set([c.r, c.g, c.b], i * 3);
  geo.setAttribute('color', new BufferAttribute(a, 3));
  return geo;
}

/**
 * A soft radial gradient laid flat on the floor with additive blending. This is
 * how the reference sheet's pooled light is achieved without paying for a real
 * shadow-casting point light per fixture — we can afford hundreds of these.
 *
 * The catch, and it is a big one: these STACK. Each one is additive and nothing
 * clamps the sum, so a spot on the floor under a couple of ceiling strips, a
 * fitting and a concourse downlight quietly receives five of them and goes
 * milky — which is exactly what turned the clothing store into a box of custard,
 * and it took a `pick` at the floor to find, because on screen it just looks
 * like the floor material is too pale.
 *
 * Two habits follow. Size a pool to the fitting, not to the room; a strip light
 * gets a puddle the width of the strip, not a nine-metre disc. And name them,
 * which is what `label` is for — five anonymous quads in a pick result tell you
 * nothing about which fitting to go and turn down.
 */
export function lightPool(radius: number, color: Color, intensity = 1, label = 'pool'): Mesh {
  const geo = paint(new PlaneGeometry(radius * 2, radius * 2), color.clone().multiplyScalar(intensity));
  const mesh = new Mesh(geo, glowMat());
  // In `userData` rather than in `name`, because a name is what disqualifies a
  // mesh from batching and there are enough of these for that to matter. `pick`
  // reports it, which is the only thing the label was ever for.
  mesh.userData.glow = `pool:${label}@${radius.toFixed(1)}x${intensity.toFixed(2)}`;
  mesh.rotation.x = -Math.PI / 2;
  // Float just above the floor to avoid z-fighting on coplanar tile.
  mesh.position.y = 0.012;
  mesh.renderOrder = 2;
  return mesh;
}

/**
 * The vertical companion to `lightPool` — a soft glow laid on a WALL.
 *
 * The pooled-light trick has one hole in it that took a long time to name: a
 * floor quad and an emissive fitting both light the ground and light nothing
 * else, so a corridor lit entirely that way has a bright strip of floor between
 * two absolutely black walls. That is the service corridor, which measured 79%
 * pure black — not atmospheric, just missing.
 *
 * The honest fix is a real light per fitting, and the mall cannot afford it:
 * three.js forward-renders, so all 37 point lights already in the scene are
 * evaluated by every lit fragment in the building, and the corridor alone would
 * add twenty-odd more. This costs one transparent quad instead.
 *
 * Returns a plane in the XY plane facing +z; rotate it to face into the room.
 */
export function lightWash(
  width: number,
  height: number,
  color: Color,
  intensity = 1,
  label = 'wash',
): Mesh {
  const geo = paint(new PlaneGeometry(width, height), color.clone().multiplyScalar(intensity));
  const mesh = new Mesh(geo, glowMat());
  mesh.userData.glow = `wash:${label}@${width.toFixed(1)}x${intensity.toFixed(2)}`;
  mesh.renderOrder = 2;
  return mesh;
}

function makePoolTexture() {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  // Squared falloff reads more like a real inverse-square pool than a linear ramp.
  for (let i = 0; i <= 10; i++) {
    const t = i / 10;
    grad.addColorStop(t, `rgba(255,255,255,${Math.pow(1 - t, 2.2).toFixed(4)})`);
  }
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  return new CanvasTexture(canvas);
}

// ---------------------------------------------------------------------------
// Lighting fixtures
// ---------------------------------------------------------------------------

export interface FixtureOptions {
  /** Attach a real PointLight as well as the pool. Budget these carefully. */
  realLight?: boolean;
  /** Height of the ceiling this fixture hangs from. */
  ceilingY?: number;
  intensity?: number;
  /**
   * Paint a fake pool on the ground under the fitting. On by default, and it
   * should stay on indoors — see `lightPool` for why the mall cannot afford the
   * real thing.
   *
   * Turn it OFF wherever a fitting's real light is baked into a surface the
   * player looks at along the ground. A pool is a square decal: additive, with
   * a radial falloff that reaches zero at the inscribed circle and a hard stop
   * at the quad. Underfoot at head height that edge is invisible. On a flat
   * apron seen at a grazing angle it is a straight line running clean off both
   * sides of the frame, and it slides as you walk because it is pinned in world
   * space. That is the "line on the floor" two people have now reported, and
   * measuring it settled it: hiding every additive quad took the step across
   * the seam from 9.3│15.8 to 9.3│8.4, with nothing else changed.
   */
  pool?: boolean;
}

/** Recessed fluorescent ceiling panel — the default mall light. */
export function fluorescentPanel(
  width = 1.2,
  length = 2.4,
  opts: FixtureOptions = {},
): Group {
  const g = new Group();
  const ceilingY = opts.ceilingY ?? 3.4;
  const intensity = opts.intensity ?? 1;

  const housing = box(width + 0.12, 0.1, length + 0.12, MATERIALS.plasterDim(), false);
  housing.position.y = ceilingY - 0.05;
  housing.castShadow = false;
  g.add(housing);

  /**
   * Split into ~1.2m troffers with dark gaps rather than one continuous tube.
   *
   * Callers ask for runs up to 4m long, and a 4m glowing slab is both wrong —
   * a mall ceiling is rows of discrete fittings, not a light trench — and
   * unrenderable: emissive AREA is what clips under AgX, so a quad that big
   * goes to paper white no matter how far the drive is turned down. Seen at a
   * grazing angle it becomes a white bar across the top of the frame, which is
   * exactly the blowout in the clothing and electronics shots. Segmenting it
   * gives the same amount of light with a shape, and the dark gaps between
   * segments are what make the lit parts read as bright.
   */
  const SEG = 1.2;
  const segments = Math.max(1, Math.round(length / SEG));
  const segLen = (length / segments) * 0.76;
  const tubeMat = emitter('fluoroPanel', EMIT.fluoro, 0.62 * intensity);
  for (let i = 0; i < segments; i++) {
    const tube = new Mesh(flatVertexColor(new BoxGeometry(width, 0.04, segLen)), tubeMat);
    tube.position.set(0, ceilingY - 0.11, -length / 2 + (length / segments) * (i + 0.5));
    g.add(tube);
  }

  /**
   * Sized off the SHORT dimension, capped against the long one.
   *
   * `max(width, length) * 2.1` is fine for the stock 1.2 × 2.4 troffer and
   * catastrophic for the 0.9 × 4.4 runs the shop builder asks for: that is a
   * nine-metre disc of additive glow, wider than the unit it is lighting, and
   * two of them per store. A strip light puts down a puddle roughly as wide as
   * the strip is long in the short axis — not a room-filling wash.
   */
  const pool = lightPool(
    Math.min(Math.max(width, length) * 2.1, width * 4.2),
    EMIT.fluoro,
    0.24 * intensity,
    'fluoro',
  );
  g.add(pool);

  if (opts.realLight) {
    const l = new PointLight(
      LIGHTS.fluoro.color,
      LIGHTS.fluoro.intensity * intensity,
      LIGHTS.fluoro.distance,
      LIGHTS.fluoro.decay,
    );
    l.position.set(0, ceilingY - 0.3, 0);
    g.add(l);
  }
  return g;
}

/**
 * Recessed downlight under a soffit. Unlike `fluorescentPanel` this always
 * carries a real light, because it exists specifically to put illumination on
 * the vertical surfaces around it — an atrium lit only by pooled floor quads has
 * bright ground and pitch-black everything else, so escalators, planters and
 * columns silhouette as solid shapes with no form.
 *
 * Budget these. Every one is a forward-rendered light on every fragment.
 */
export function downlight(ceilingY = 4.6, intensity = 1, opts: FixtureOptions = {}): Group {
  const g = new Group();
  const can = new Mesh(
    flatVertexColor(new CylinderGeometry(0.16, 0.2, 0.14, 8)),
    MATERIALS.steelDark(),
  );
  can.position.y = ceilingY - 0.07;
  g.add(can);

  const lens = new Mesh(
    new PlaneGeometry(0.3, 0.3),
    emitter('downlightLens', EMIT.shopWarm, 1.1 * intensity),
  );
  lens.rotation.x = Math.PI / 2;
  lens.position.y = ceilingY - 0.145;
  g.add(lens);

  const l = new PointLight(LIGHTS.shop.color, 11 * intensity, 15, 1.7);
  l.position.y = ceilingY - 0.3;
  g.add(l);

  if (opts.pool !== false) g.add(lightPool(3.4, EMIT.shopWarm, 0.24 * intensity));
  return g;
}

/**
 * Suspended pendant on a long drop — for volumes too tall to light from the
 * ceiling.
 *
 * The food court is ten and a half metres to the glazing and everything lighting
 * it lived at counter height, so above four metres the frame was an unbroken
 * black band with a skylight grid floating in it. A recessed fitting cannot fix
 * that: it would be up in the dark with the ceiling. A pendant hangs its source
 * down into the middle of the volume, which both lights the tables and puts an
 * object in the empty half of the frame — the cable is doing as much work as
 * the bulb.
 *
 * `dropTo` is the height of the shade, not the length of the cable, because
 * that is the number the composition cares about.
 */
export function pendantLamp(ceilingY: number, dropTo: number, opts: FixtureOptions = {}): Group {
  const g = new Group();
  const intensity = opts.intensity ?? 1;

  const cable = new Mesh(
    flatVertexColor(new CylinderGeometry(0.018, 0.018, ceilingY - dropTo, 5)),
    MATERIALS.steelDark(),
  );
  cable.position.y = (ceilingY + dropTo) / 2;
  g.add(cable);

  // Wide shallow cone. A deep shade at this height hides its own bulb from the
  // floor and the fitting reads as a dark disc hanging in the dark.
  const shade = new Mesh(
    applyContactShading(new CylinderGeometry(0.44, 0.12, 0.3, 10, 1, true), {
      falloff: 0.3,
      strength: 0.35,
    }),
    MATERIALS.plasterDim(),
  );
  shade.position.y = dropTo + 0.15;
  g.add(shade);

  const bulb = new Mesh(
    flatVertexColor(new SphereGeometry(0.12, 8, 6)),
    emitter('pendantBulb', EMIT.shopWarm, 2.2 * intensity),
  );
  bulb.position.y = dropTo + 0.02;
  g.add(bulb);

  // No y offset: the group's origin is on the floor by convention (see the top
  // of this file) and `lightPool` already sits just above it.
  g.add(lightPool(6.0, EMIT.shopWarm, 0.4 * intensity, 'pendant'));

  if (opts.realLight) {
    const l = new PointLight(LIGHTS.shop.color, 16 * intensity, 22, 1.7);
    l.position.y = dropTo;
    g.add(l);
  }
  return g;
}

/** Sodium vapour lamp on a bracket — parking structure only. */
export function sodiumLamp(opts: FixtureOptions = {}): Group {
  const g = new Group();
  const ceilingY = opts.ceilingY ?? 2.9;

  const bracket = box(0.08, 0.5, 0.08, MATERIALS.steelDark(), false);
  bracket.position.y = ceilingY - 0.25;
  g.add(bracket);

  const shade = new Mesh(
    flatVertexColor(new CylinderGeometry(0.34, 0.16, 0.22, 8, 1, true)),
    MATERIALS.steelDark(),
  );
  shade.position.y = ceilingY - 0.55;
  g.add(shade);

  const bulb = new Mesh(
    flatVertexColor(new SphereGeometry(0.13, 8, 6)),
    emitter('sodiumBulb', EMIT.sodium, 2.4),
  );
  bulb.position.y = ceilingY - 0.62;
  g.add(bulb);

  g.add(lightPool(4.6, EMIT.sodium, 0.78));

  if (opts.realLight) {
    const l = new PointLight(
      LIGHTS.sodium.color,
      LIGHTS.sodium.intensity,
      LIGHTS.sodium.distance,
      LIGHTS.sodium.decay,
    );
    l.position.set(0, ceilingY - 0.7, 0);
    g.add(l);
  }
  return g;
}

/** Green running-man EXIT box over a door. */
export function exitSign(): Group {
  const g = new Group();
  const body = box(0.52, 0.2, 0.06, MATERIALS.steelDark(), false);
  g.add(body);
  const face = new Mesh(
    new PlaneGeometry(0.46, 0.15),
    signMaterial({ text: 'EXIT', widthMetres: 0.46, heightMetres: 0.15, color: EMIT.exitGreen, font: 'wide' }),
  );
  face.position.z = 0.032;
  g.add(face);
  const back = face.clone();
  back.rotation.y = Math.PI;
  back.position.z = -0.032;
  g.add(back);
  return g;
}

// ---------------------------------------------------------------------------
// Storefronts
// ---------------------------------------------------------------------------

export interface StorefrontOptions {
  width: number;
  height?: number;
  /** Store name as it appears on the fascia. Uppercase, deadpan. */
  name: string;
  signColor?: Color;
  signFont?: SignOptions['font'];
  /** Secondary line under the name — the "BUY MORE FOR NO REASON" slot. */
  tagline?: string;
  /** Is the unit lit from inside? Dark units read as closed and are good cover. */
  lit?: boolean;
  /** Roller shutter down means the thief needs to force it. */
  shutter?: 'open' | 'half' | 'closed';
  flicker?: number;
  /** Walk-in gap at the centre of the glazing. 0 for a sealed display window. */
  entranceWidth?: number;
  /**
   * Build the shallow fake interior. The mall builder turns this off because it
   * constructs a real, enterable room behind the glazing instead — but a lone
   * storefront in a test scene still wants the cheap version.
   */
  interior?: boolean;
}

/**
 * A shopfront: bulkhead above, fascia sign, glazed front with mullions, and an
 * optional roller shutter. Built in the XY plane facing +Z so it can be dropped
 * onto any wall segment by the mall builder.
 */
export function storefront(opts: StorefrontOptions): Group {
  const g = new Group();
  const w = opts.width;
  const h = opts.height ?? 4.0;
  const fasciaH = 0.9;
  const glazeH = h - fasciaH;

  // Bulkhead / fascia panel
  const fascia = box(w, fasciaH, 0.35, MATERIALS.plaster());
  fascia.position.set(0, h - fasciaH / 2, 0);
  g.add(fascia);

  // Fascia sign. Sized to nearly fill the fascia — in the reference sheet the
  // store name is the loudest thing in frame after the light fixtures.
  const signH = fasciaH * 0.7;
  const signW = Math.min(w * 0.94, opts.name.length * signH * 0.58 + signH);
  const sign = new Mesh(
    new PlaneGeometry(signW, signH),
    signMaterial({
      text: opts.name,
      widthMetres: signW,
      heightMetres: signH,
      color: opts.signColor ?? EMIT.signAmber,
      font: opts.signFont ?? 'condensed',
      flicker: opts.flicker,
    }),
  );
  sign.position.set(0, h - fasciaH / 2, 0.18);
  g.add(sign);

  if (opts.tagline) {
    const tH = 0.16;
    const tW = Math.min(w * 0.8, opts.tagline.length * tH * 0.6 + tH);
    const tag = new Mesh(
      new PlaneGeometry(tW, tH),
      signMaterial({
        text: opts.tagline,
        widthMetres: tW,
        heightMetres: tH,
        color: (opts.signColor ?? EMIT.signAmber).clone().multiplyScalar(0.7),
        font: 'wide',
      }),
    );
    tag.position.set(0, h - fasciaH - 0.22, 0.18);
    g.add(tag);
  }

  // Glazing with mullions every ~1.6m, interrupted by the entrance. Glass is two
  // panes rather than one so the doorway is a genuine hole you can walk through,
  // not a transparent surface the player bounces off.
  const door = Math.max(0, Math.min(opts.entranceWidth ?? 0, w - 0.4));
  const paneW = (w - door) / 2;
  for (const side of [-1, 1] as const) {
    if (paneW < 0.05) continue;
    const cx = side * (door / 2 + paneW / 2);
    const mullions = Math.max(1, Math.round(paneW / 1.6));
    for (let i = 0; i <= mullions; i++) {
      const m = box(0.07, glazeH, 0.14, MATERIALS.steel(), false);
      m.position.set(cx - paneW / 2 + (paneW / mullions) * i, glazeH / 2, 0);
      g.add(m);
    }
    const glass = new Mesh(new PlaneGeometry(paneW, glazeH), MATERIALS.glass());
    glass.position.set(cx, glazeH / 2, 0.01);
    g.add(glass);
  }
  if (door > 0) {
    // Header over the doorway, so the opening reads as designed rather than as a
    // missing panel.
    const header = box(door, 0.22, 0.16, MATERIALS.steel(), false);
    header.position.set(0, glazeH - 0.11, 0);
    g.add(header);
  }

  // Interior. A lit unit needs a back wall, a floor, and a spill plane, or the
  // glazing reads as a black hole punched in the wall rather than a shop.
  if (opts.interior === false) {
    if (opts.lit !== false) {
      const outPool = lightPool(w * 0.7, EMIT.shopWarm, 0.34);
      outPool.position.z = 1.3;
      g.add(outPool);
    }
  } else if (opts.lit !== false) {
    const backWall = box(w, h, 0.1, MATERIALS.plaster(), false);
    backWall.position.set(0, h / 2, -3.6);
    g.add(backWall);

    const innerFloor = new Mesh(
      flatVertexColor(new PlaneGeometry(w, 3.6)),
      MATERIALS.tileDark(),
    );
    innerFloor.rotation.x = -Math.PI / 2;
    innerFloor.position.set(0, 0.01, -1.8);
    innerFloor.receiveShadow = true;
    g.add(innerFloor);

    const spill = new Mesh(
      new PlaneGeometry(w * 0.95, glazeH * 0.9),
      emitter('shopInterior', EMIT.shopWarm, 0.5),
    );
    spill.position.set(0, glazeH * 0.5, -3.5);
    g.add(spill);

    // Ceiling strip inside the unit — gives the interior its own pooled light.
    const strip = new Mesh(
      flatVertexColor(new BoxGeometry(w * 0.5, 0.05, 0.3)),
      emitter('shopStrip', EMIT.shopWarm, 0.8),
    );
    strip.position.set(0, glazeH - 0.25, -1.8);
    g.add(strip);

    const inPool = lightPool(w * 0.7, EMIT.shopWarm, 0.5);
    inPool.position.z = -1.8;
    g.add(inPool);

    // Spill onto the mall walkway outside the unit.
    const outPool = lightPool(w * 0.7, EMIT.shopWarm, 0.34);
    outPool.position.z = 1.3;
    g.add(outPool);
  } else {
    // Dark units still need a back wall so the player cannot see through the mall.
    const backWall = box(w, h, 0.1, MATERIALS.plasterDim(), false);
    backWall.position.set(0, h / 2, -3.6);
    g.add(backWall);
  }

  // Roller shutter
  if (opts.shutter && opts.shutter !== 'open') {
    const shutterH = opts.shutter === 'closed' ? glazeH : glazeH * 0.45;
    const slats = Math.max(1, Math.round(shutterH / 0.14));
    for (let i = 0; i < slats; i++) {
      const s = box(w - 0.06, 0.12, 0.05, MATERIALS.steelDark(), false);
      s.position.set(0, glazeH - i * 0.14 - 0.06, 0.1);
      g.add(s);
    }
  }

  return g;
}

// ---------------------------------------------------------------------------
// Population
// ---------------------------------------------------------------------------

/**
 * Uncanny retail mannequin. Featureless, pale clay, slightly wrong proportions —
 * the reference sheet uses these as the only "people" in frame, and their job is
 * to make the player second-guess whether something moved.
 */
export function mannequin(pose: 'stand' | 'contrapposto' | 'armsOut' = 'stand'): Group {
  const g = new Group();
  const mat = MATERIALS.mannequin();

  // Torso via lathe so it tapers like a dress form rather than reading as a box.
  const profile: Vector2[] = [
    new Vector2(0.0, 0.0),
    new Vector2(0.17, 0.02),
    new Vector2(0.2, 0.22),
    new Vector2(0.15, 0.48),
    new Vector2(0.19, 0.66),
    new Vector2(0.16, 0.78),
    new Vector2(0.0, 0.8),
  ];
  const torso = new Mesh(applyContactShading(new LatheGeometry(profile, 7), { falloff: 0.8, strength: 0.3 }), mat);
  torso.position.y = 0.94;
  torso.castShadow = true;
  g.add(torso);

  const head = new Mesh(flatVertexColor(new SphereGeometry(0.115, 7, 5)), mat);
  head.position.y = 1.86;
  head.scale.set(1, 1.22, 0.92);
  head.castShadow = true;
  g.add(head);

  const neck = box(0.07, 0.12, 0.07, mat, false);
  neck.position.y = 1.75;
  g.add(neck);

  const leg = (side: number) => {
    const l = new Mesh(
      applyContactShading(new CylinderGeometry(0.075, 0.055, 0.94, 6), { falloff: 0.9, strength: 0.5 }),
      mat,
    );
    l.position.set(side * 0.1, 0.47, 0);
    l.castShadow = true;
    return l;
  };
  g.add(leg(-1), leg(1));

  const arm = (side: number, outward: number) => {
    const a = new Mesh(flatVertexColor(new CylinderGeometry(0.052, 0.042, 0.72, 6)), mat);
    a.position.set(side * 0.24, 1.36, 0);
    a.rotation.z = side * outward;
    a.castShadow = true;
    return a;
  };

  if (pose === 'armsOut') {
    g.add(arm(-1, 0.95), arm(1, 0.95));
  } else {
    g.add(arm(-1, 0.13), arm(1, 0.1));
  }
  if (pose === 'contrapposto') {
    g.rotation.y = 0.28;
    torso.rotation.z = 0.05;
  }
  return g;
}

// ---------------------------------------------------------------------------
// Architecture pieces
// ---------------------------------------------------------------------------

/** Glass-and-steel balustrade for atrium edges and upper walkways. */
export function railing(length: number, opts: { glass?: boolean } = {}): Group {
  const g = new Group();
  const h = 1.05;
  const posts = Math.max(2, Math.round(length / 1.8));
  for (let i = 0; i <= posts; i++) {
    const x = -length / 2 + (length / posts) * i;
    const p = box(0.06, h, 0.06, MATERIALS.steel(), false);
    p.position.set(x, h / 2, 0);
    g.add(p);
  }
  const top = box(length, 0.07, 0.11, MATERIALS.steel(), false);
  top.position.y = h;
  g.add(top);

  if (opts.glass !== false) {
    const panel = new Mesh(new PlaneGeometry(length - 0.1, h - 0.16), MATERIALS.balustrade());
    panel.position.y = (h - 0.16) / 2 + 0.04;
    g.add(panel);
  }
  return g;
}

/** Planter with fake foliage — reference panel 5 labels them "PLANTS (FAKE)". */
export function planter(radius = 1.4): Group {
  const g = new Group();
  const tub = new Mesh(
    applyContactShading(new CylinderGeometry(radius, radius * 0.92, 0.55, 8), {
      falloff: 0.55,
      strength: 0.4,
    }),
    MATERIALS.concreteDark(),
  );
  tub.position.y = 0.275;
  tub.castShadow = true;
  tub.receiveShadow = true;
  g.add(tub);

  const soil = new Mesh(flatVertexColor(new CylinderGeometry(radius * 0.9, radius * 0.9, 0.04, 8)), MATERIALS.rubber());
  soil.position.y = 0.55;
  g.add(soil);

  const trunk = new Mesh(flatVertexColor(new CylinderGeometry(0.09, 0.13, 1.9, 6)), MATERIALS.timber());
  trunk.position.y = 1.5;
  trunk.castShadow = true;
  g.add(trunk);

  // Foliage as separated clusters on a spiral rather than stacked concentric
  // discs. Concentric discs overlap so heavily they weld into a single green
  // cone, which is the one silhouette a tree must never have. Offsetting each
  // cluster off-axis keeps the gaps between them readable.
  // Sized off the trunk, not off the tub. A canopy scaled to a 1.5m planter is a
  // three-metre ball of leaves that swallows the shot it stands in.
  const R = 0.34;
  const CLUSTERS: Array<[number, number, number, number]> = [
    // [angle turns, radial offset, height, size]
    [0.0, 0.62, 2.16, 1.0],
    [0.28, 0.7, 2.38, 0.86],
    [0.55, 0.58, 2.1, 0.8],
    [0.79, 0.66, 2.3, 0.9],
    [0.14, 0.36, 2.62, 0.9],
    [0.46, 0.42, 2.74, 0.82],
    [0.68, 0.3, 2.5, 0.76],
    [0.92, 0.4, 2.66, 0.8],
    [0.36, 0.1, 2.94, 0.78],
  ];
  for (const [turn, off, y, size] of CLUSTERS) {
    const a = turn * Math.PI * 2;
    const blob = new Mesh(flatVertexColor(new SphereGeometry(R * size, 5, 4)), MATERIALS.foliage());
    blob.position.set(Math.cos(a) * off, y, Math.sin(a) * off);
    blob.scale.set(1, 0.72, 1);
    blob.rotation.y = a;
    blob.castShadow = true;
    g.add(blob);
  }
  return g;
}

/** Food-court table with four chairs (reference panel 8). */
export function cafeTable(): Group {
  const g = new Group();
  const top = new Mesh(flatVertexColor(new CylinderGeometry(0.42, 0.42, 0.05, 10)), MATERIALS.timber());
  top.position.y = 0.74;
  top.castShadow = true;
  top.receiveShadow = true;
  g.add(top);
  const stem = box(0.07, 0.72, 0.07, MATERIALS.steelDark(), false);
  stem.position.y = 0.36;
  g.add(stem);
  const foot = new Mesh(flatVertexColor(new CylinderGeometry(0.3, 0.32, 0.04, 10)), MATERIALS.steelDark());
  foot.position.y = 0.02;
  g.add(foot);

  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + 0.4;
    const chair = new Group();
    const seat = box(0.4, 0.05, 0.4, MATERIALS.plasterDim());
    seat.position.y = 0.45;
    chair.add(seat);
    const back = box(0.4, 0.45, 0.05, MATERIALS.plasterDim());
    back.position.set(0, 0.68, -0.18);
    chair.add(back);
    for (const [lx, lz] of [[-0.16, -0.16], [0.16, -0.16], [-0.16, 0.16], [0.16, 0.16]]) {
      const leg = box(0.04, 0.45, 0.04, MATERIALS.steelDark(), false);
      leg.position.set(lx, 0.225, lz);
      chair.add(leg);
    }
    chair.position.set(Math.cos(a) * 0.95, 0, Math.sin(a) * 0.95);
    chair.rotation.y = -a + Math.PI / 2;
    g.add(chair);
  }
  return g;
}

/**
 * Escalator pair. Purely visual geometry plus a walkable ramp collider supplied
 * separately by the mall builder — we never simulate moving steps.
 */
export function escalator(rise: number, run: number, width = 1.2): Group {
  const g = new Group();
  const angle = Math.atan2(rise, run);
  const length = Math.hypot(rise, run);

  // Truss underside
  const truss = box(width + 0.3, 0.5, length, MATERIALS.steelDark(), false);
  truss.position.set(0, rise / 2 - 0.3, run / 2);
  truss.rotation.x = -angle;
  g.add(truss);

  // Step band. Tread AND riser, not a single sloped slab: the riser is what
  // casts the thin shadow line between steps, and without those lines the whole
  // deck reads as one black wedge from anywhere at eye level.
  const steps = Math.max(4, Math.round(length / 0.42));
  for (let i = 0; i < steps; i++) {
    const t = i / steps;
    const step = box(width, 0.08, 0.42, MATERIALS.steel(), false);
    step.position.set(0, rise * t + 0.06, run * t + 0.21);
    step.rotation.x = -angle;
    g.add(step);

    const riser = box(width, 0.16, 0.05, MATERIALS.steelDark(), false);
    riser.position.set(0, rise * t + 0.02, run * t + 0.42);
    riser.rotation.x = -angle;
    g.add(riser);
  }

  // Balustrades.
  // Order matters: the pane is authored long-axis-along-X, so it must be yawed
  // into the run direction FIRST and tilted about world X second. Three composes
  // 'XYZ' as Rx·Ry, which is that order. Using 'YXZ' tilts the pane's normal
  // instead of the pane, and you get a vertical slab standing beside the
  // escalator rather than a balustrade climbing with it.
  for (const side of [-1, 1]) {
    const balus = new Mesh(new PlaneGeometry(length, 0.95), MATERIALS.balustrade());
    balus.position.set((side * (width + 0.16)) / 2, rise / 2 + 0.55, run / 2);
    balus.rotation.set(-angle, Math.PI / 2, 0, 'XYZ');
    g.add(balus);

    const handrail = box(0.09, 0.07, length, MATERIALS.rubber(), false);
    handrail.position.set((side * (width + 0.16)) / 2, rise / 2 + 1.03, run / 2);
    handrail.rotation.x = -angle;
    g.add(handrail);

    // Skirt lighting along the base of the balustrade. Real escalators have it,
    // and without it the whole assembly is a featureless dark diagonal — this
    // one emissive line is what gives the atrium's strongest shape an edge.
    const skirt = new Mesh(
      new PlaneGeometry(length, 0.05),
      emitter('escalatorSkirt', EMIT.fluoro, 0.7),
    );
    skirt.position.set((side * (width + 0.26)) / 2, rise / 2 + 0.12, run / 2);
    skirt.rotation.set(-angle, side * (Math.PI / 2), 0, 'XYZ');
    g.add(skirt);
  }

  // Comb plates. Two bright horizontal bars are what tell you at a glance where
  // the thing starts and where it ends — the escalator is the main vertical
  // route in the building and it has to advertise both of its mouths.
  for (const [y, z] of [
    [0.05, -0.05],
    [rise + 0.05, run + 0.05],
  ]) {
    const comb = box(width + 0.1, 0.06, 0.5, MATERIALS.steel(), false);
    comb.position.set(0, y, z);
    g.add(comb);
  }

  // One light per escalator, hung under the handrail at mid-run. The skirt
  // strips are emissive quads and light nothing; this is what actually puts a
  // sheen on the treads so the deck reads as steps instead of a black ramp.
  const lamp = new PointLight(EMIT.fluoro, 5.5, length * 0.9, 1.9);
  lamp.position.set(0, rise / 2 + 1.1, run / 2);
  g.add(lamp);

  return g;
}

/** Dome security camera. Rotates via `lookAt` on its yoke from the security sim. */
export function securityCamera(): Group {
  const g = new Group();
  const mount = box(0.06, 0.16, 0.06, MATERIALS.steelDark(), false);
  mount.position.y = -0.08;
  g.add(mount);

  const yoke = new Group();
  yoke.name = 'yoke';
  const body = new Mesh(flatVertexColor(new CylinderGeometry(0.09, 0.11, 0.2, 8)), MATERIALS.plasterDim());
  body.rotation.z = Math.PI / 2;
  yoke.add(body);
  const lens = new Mesh(flatVertexColor(new CylinderGeometry(0.055, 0.065, 0.08, 8)), MATERIALS.rubber());
  lens.rotation.x = Math.PI / 2;
  lens.position.z = 0.13;
  yoke.add(lens);

  // Status LED — turns red in the security sim when this camera has a target.
  const led = new Mesh(flatVertexColor(new SphereGeometry(0.018, 6, 4)), emitter('camLed', EMIT.exitGreen, 2.2));
  led.name = 'led';
  led.position.set(0.06, 0.06, 0.09);
  yoke.add(led);

  yoke.position.y = -0.2;
  g.add(yoke);
  return g;
}

/** Freestanding directory / wayfinding pylon. */
export function directoryPylon(text: string): Group {
  const g = new Group();
  const body = box(0.9, 2.0, 0.18, MATERIALS.plasterDim());
  body.position.y = 1.0;
  g.add(body);
  const screen = new Mesh(
    new PlaneGeometry(0.78, 1.6),
    signMaterial({ text, widthMetres: 0.78, heightMetres: 1.6, color: EMIT.signCyan, font: 'wide', align: 'left' }),
  );
  screen.position.set(0, 1.05, 0.095);
  g.add(screen);
  g.add(lightPool(1.6, EMIT.signCyan, 0.28));
  return g;
}

/**
 * Illuminated letters on a standoff frame, for the front of the building.
 * Deliberately oversized: the approach shot is the first thing a player sees and
 * the reference sheet's portal panels all work because one enormous piece of
 * corporate typography establishes the scale before anything else does.
 */
export function facadeSign(text: string, widthMetres = 12, heightMetres = 1.6, color = EMIT.signCyan): Group {
  const g = new Group();
  const backer = box(widthMetres + 0.6, heightMetres + 0.5, 0.24, MATERIALS.steelDark(), false);
  g.add(backer);
  const face = new Mesh(
    new PlaneGeometry(widthMetres, heightMetres),
    signMaterial({ text, widthMetres, heightMetres, color, font: 'wide', pixelsPerMetre: 96 }),
  );
  face.position.z = 0.14;
  g.add(face);
  return g;
}

/** Steel bollard. In fives, outside a door, because that is where they live. */
export function bollard(height = 0.95): Group {
  const g = new Group();
  const post = new Mesh(
    applyContactShading(new CylinderGeometry(0.11, 0.13, height, 8), { falloff: 0.7, strength: 0.4 }),
    MATERIALS.steel(),
  );
  post.position.y = height / 2;
  post.castShadow = true;
  g.add(post);
  const cap = new Mesh(flatVertexColor(new SphereGeometry(0.11, 8, 4)), MATERIALS.steel());
  cap.position.y = height;
  cap.scale.y = 0.6;
  g.add(cap);
  return g;
}

/** Litter bin. Reads as inhabited space without needing an inhabitant. */
export function litterBin(): Group {
  const g = new Group();
  const body = new Mesh(
    applyContactShading(new CylinderGeometry(0.32, 0.27, 0.9, 8, 1, true), { falloff: 0.8, strength: 0.4 }),
    MATERIALS.steelDark(),
  );
  body.position.y = 0.45;
  body.castShadow = true;
  g.add(body);
  const lid = new Mesh(flatVertexColor(new CylinderGeometry(0.35, 0.35, 0.08, 8)), MATERIALS.steel());
  lid.position.y = 0.94;
  g.add(lid);
  return g;
}

/**
 * Wheeled stock cage. Back-of-house filler, and cover: it is roughly a crouching
 * player's width, so a corridor lined with these is a corridor with places to
 * break line of sight in.
 *
 * The mesh sides are drawn as bars rather than a transparent panel deliberately.
 * A cage you can see through is worth far more than a solid box here — a guard
 * sweeping the corridor should get a glimpse of movement behind one, not a clean
 * yes or no.
 */
export function stockCage(width = 1.0, height = 1.9, depth = 1.2): Group {
  const g = new Group();
  const frame = MATERIALS.steelDark();
  const post = (x: number, z: number) => {
    const p = box(0.05, height, 0.05, frame, false);
    p.position.set(x, height / 2, z);
    g.add(p);
  };
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) post(sx * width / 2, sz * depth / 2);

  const deck = box(width, 0.06, depth, MATERIALS.steel(), false);
  deck.position.y = 0.18;
  g.add(deck);

  // Horizontal bars on three sides; the fourth is the open end.
  for (let i = 0; i < 5; i++) {
    const y = 0.3 + i * ((height - 0.4) / 4);
    for (const sx of [-1, 1]) {
      const bar = box(0.03, 0.03, depth, frame, false);
      bar.position.set((sx * width) / 2, y, 0);
      g.add(bar);
    }
    const back = box(width, 0.03, 0.03, frame, false);
    back.position.set(0, y, -depth / 2);
    g.add(back);
  }

  // Loose stock on the deck, so the cage reads as in use rather than stored.
  for (let i = 0; i < 3; i++) {
    const b = box(width * 0.4, 0.26, depth * 0.4, MATERIALS.cardboard(), false);
    b.position.set((i % 2 ? 0.2 : -0.2) * width, 0.34 + i * 0.27, (i % 2 ? -0.15 : 0.15) * depth);
    b.rotation.y = 0.2 * (i - 1);
    g.add(b);
  }

  for (const [wx, wz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
    const wheel = new Mesh(flatVertexColor(new CylinderGeometry(0.07, 0.07, 0.04, 6)), MATERIALS.rubber());
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set((wx * width) / 2.4, 0.07, (wz * depth) / 2.6);
    g.add(wheel);
  }
  return g;
}

/** Stack of pallets and boxes against a wall. Pure silhouette filler. */
export function palletStack(height = 1.2): Group {
  const g = new Group();
  const layers = Math.max(2, Math.round(height / 0.16));
  for (let i = 0; i < layers; i++) {
    const p = box(1.2, 0.12, 1.0, MATERIALS.timber(), false);
    // A drunken stack, not a surveyed one — the offset is what makes it read.
    p.position.set(((i % 3) - 1) * 0.05, 0.06 + i * 0.15, ((i % 2) - 0.5) * 0.06);
    p.rotation.y = ((i % 5) - 2) * 0.03;
    g.add(p);
  }
  const crown = box(1.0, 0.5, 0.86, MATERIALS.cardboard(), false);
  crown.position.set(0.04, layers * 0.15 + 0.31, -0.02);
  crown.rotation.y = 0.09;
  g.add(crown);
  return g;
}

/**
 * Car park lamp standard — a column, not a ceiling bracket, so it works outside
 * where there is nothing overhead to hang a `sodiumLamp` from.
 */
export function lampPost(height = 5.4, opts: FixtureOptions = {}): Group {
  const g = new Group();
  const column = new Mesh(
    applyContactShading(new CylinderGeometry(0.08, 0.15, height, 8), { falloff: 1.2, strength: 0.45 }),
    MATERIALS.steelDark(),
  );
  column.position.y = height / 2;
  column.castShadow = true;
  g.add(column);

  const head = box(0.5, 0.14, 0.9, MATERIALS.steelDark(), false);
  head.position.set(0, height, 0.35);
  g.add(head);

  const lens = new Mesh(
    new PlaneGeometry(0.42, 0.8),
    emitter('lampLens', EMIT.sodium, 1.9),
  );
  lens.rotation.x = Math.PI / 2;
  lens.position.set(0, height - 0.08, 0.35);
  g.add(lens);

  if (opts.pool !== false) {
    const pool = lightPool(5.2, EMIT.sodium, 0.62);
    pool.position.z = 0.35;
    g.add(pool);
  }

  if (opts.realLight) {
    const l = new PointLight(
      LIGHTS.sodium.color,
      LIGHTS.sodium.intensity,
      LIGHTS.sodium.distance,
      LIGHTS.sodium.decay,
    );
    l.position.set(0, height - 0.3, 0.35);
    g.add(l);
  }
  return g;
}

/**
 * Painted markings on the forecourt, as one textured plane rather than forty
 * thin boxes.
 *
 * The approach shot measured 65% black and half of that was the ground: a bare
 * asphalt plane in front of the doors with nothing on it, so the bottom third
 * of the player's very first frame was an unbroken dark band. Paint is the
 * cheapest possible fix — it costs one draw call, it gives the eye a
 * perspective grid running toward the entrance, and it is the correct
 * vocabulary for the space (panels 2 and 7 are both mostly line-marking).
 *
 * Deliberately NOT emissive. Road paint is retroreflective, not lit: it has to
 * take the lamp posts' light and go dark where they do not reach, or the whole
 * forecourt reads as a glowing decal laid over the tarmac.
 */
export function forecourtMarkings(width: number, depth: number): Mesh {
  const PPM = 24;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * PPM);
  canvas.height = Math.round(depth * PPM);
  const ctx = canvas.getContext('2d')!;
  const m = (v: number) => v * PPM;
  // Canvas top is the DOOR edge. `flipY` puts row 0 at v = 1, and rotating the
  // plane -90° about x maps local +y to world -z, so anything drawn at the top
  // of this canvas lands nearest the building. Everything below depends on it.

  /**
   * Worn, and much fainter than paint looks in daylight. The first attempt used
   * 0.72 alpha over the full width and the entrance turned into a zebra
   * crossing: at four metres from the camera the markings occupied the whole
   * lower third of the frame and out-contrasted the doors they were supposed to
   * lead you to. Paint is a texture here, not a graphic.
   */
  const PAINT = (a: number) => `rgba(206,198,180,${a})`;

  /**
   * A clear walking lane straight down the middle, hatched islands either side.
   *
   * The centre of the frame is where the player is going, so it stays empty and
   * the marking sits where the eye is not. Two solid lane lines converging on
   * the doors do all the perspective work a full grid was doing, at a fraction
   * of the contrast.
   */
  const LANE = m(9);
  const laneL = (canvas.width - LANE) / 2;
  const laneR = laneL + LANE;
  ctx.strokeStyle = PAINT(0.4);
  ctx.lineWidth = m(0.13);
  for (const x of [laneL, laneR]) {
    ctx.beginPath();
    ctx.moveTo(x, m(0.5));
    ctx.lineTo(x, canvas.height - m(0.5));
    ctx.stroke();
  }

  const islandD = m(5.4);
  const islandZ = m(0.9);
  ctx.strokeStyle = PAINT(0.3);
  for (const [x0, x1] of [
    [m(1.2), laneL - m(1.0)],
    [laneR + m(1.0), canvas.width - m(1.2)],
  ]) {
    ctx.lineWidth = m(0.11);
    ctx.strokeRect(x0, islandZ, x1 - x0, islandD);
    ctx.save();
    ctx.beginPath();
    ctx.rect(x0, islandZ, x1 - x0, islandD);
    ctx.clip();
    ctx.lineWidth = m(0.08);
    for (let x = -islandD; x < x1 - x0 + islandD; x += m(1.1)) {
      ctx.beginPath();
      ctx.moveTo(x0 + x, islandZ);
      ctx.lineTo(x0 + x + islandD, islandZ + islandD);
      ctx.stroke();
    }
    ctx.restore();
  }

  // Bays, well back from the doors and outboard of the lane. Their long axis
  // runs with the view, so in perspective they converge on the entrance instead
  // of striping across it.
  ctx.strokeStyle = PAINT(0.26);
  ctx.lineWidth = m(0.1);
  for (const side of [0, 1]) {
    const x0 = side === 0 ? m(1.0) : canvas.width - m(5.6);
    const zTop = islandZ + islandD + m(1.6);
    for (let z = zTop; z < canvas.height - m(0.6); z += m(2.6)) {
      ctx.beginPath();
      ctx.moveTo(x0, z);
      ctx.lineTo(x0 + m(4.6), z);
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.moveTo(side === 0 ? x0 + m(4.6) : x0, zTop);
    ctx.lineTo(side === 0 ? x0 + m(4.6) : x0, canvas.height - m(0.6));
    ctx.stroke();
  }

  // One legend, hard up against the door end. Placed midway down the lane it
  // landed three metres in front of the camera and read as a billboard lying on
  // the tarmac; at the threshold it is seven metres out and behaves like paint.
  ctx.fillStyle = PAINT(0.34);
  ctx.font = `${m(0.45)}px "Haettenschweiler", "Arial Narrow", Impact, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('KEEP CLEAR', canvas.width / 2, islandZ + m(0.9));

  const tex = new CanvasTexture(canvas);
  tex.anisotropy = 8;
  const mesh = new Mesh(
    new PlaneGeometry(width, depth),
    new MeshStandardMaterial({
      map: tex,
      transparent: true,
      roughness: 0.92,
      metalness: 0,
      // Paint sits on the tarmac, not above it. Without the offset the quad
      // z-fights the plaza floor along the whole horizon line.
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
  );
  mesh.name = 'forecourtMarkings';
  mesh.rotation.x = -Math.PI / 2;
  mesh.receiveShadow = true;
  return mesh;
}

/** Shop shelving unit — the thing items actually sit on. */
export function shelvingUnit(width = 1.8, height = 1.9, depth = 0.5, tiers = 4): Group {
  const g = new Group();
  const frameMat = MATERIALS.steelDark();
  for (const side of [-1, 1]) {
    const upright = box(0.05, height, depth, frameMat, false);
    upright.position.set((side * width) / 2, height / 2, 0);
    g.add(upright);
  }
  for (let i = 0; i < tiers; i++) {
    const y = 0.18 + (i * (height - 0.3)) / (tiers - 1);
    const shelf = box(width, 0.035, depth, MATERIALS.plasterDim(), false);
    shelf.position.y = y;
    shelf.receiveShadow = true;
    g.add(shelf);
  }
  const back = box(width, height, 0.02, MATERIALS.plasterDim(), false);
  back.position.set(0, height / 2, -depth / 2);
  g.add(back);
  return g;
}

/** Glass display case for high-value goods — jewellery, electronics. */
export function displayCase(width = 1.6, depth = 0.7, height = 1.0): Group {
  const g = new Group();
  const plinth = box(width, height * 0.55, depth, MATERIALS.plasterDim());
  plinth.position.y = (height * 0.55) / 2;
  g.add(plinth);

  const caseH = height * 0.45;
  const glassMat = MATERIALS.glass();
  const faces: Array<[number, number, number, number, number]> = [
    [width, caseH, 0, 0, depth / 2],
    [width, caseH, 0, Math.PI, -depth / 2],
    [depth, caseH, width / 2, Math.PI / 2, 0],
    [depth, caseH, -width / 2, -Math.PI / 2, 0],
  ];
  for (const [fw, fh, x, ry, z] of faces) {
    const pane = new Mesh(new PlaneGeometry(fw, fh), glassMat);
    pane.position.set(x, height * 0.55 + fh / 2, z);
    pane.rotation.y = ry;
    g.add(pane);
  }
  const lid = box(width, 0.04, depth, MATERIALS.steel(), false);
  lid.position.y = height;
  g.add(lid);

  // Internal accent light so the case glows from within.
  const inner = new Mesh(new PlaneGeometry(width * 0.9, depth * 0.9), emitter('caseGlow', EMIT.fluoro, 0.5));
  inner.rotation.x = -Math.PI / 2;
  inner.position.y = height * 0.56;
  g.add(inner);
  return g;
}

export { V };
